import { useEffect, useState, useCallback } from "react";
import type { Deal, Stage } from "./types";
import { listDeals, saveDeal, deleteDeal as repoDelete, baseDeal } from "./lib/deals";
import { isCloud } from "./lib/supabase";
import { getSession, onAuthChange, signOut, type Session } from "./lib/auth";
import { Portfolio } from "./components/Portfolio";
import { DealWorkspace } from "./components/DealWorkspace";
import { Dashboard } from "./components/Dashboard";
import { Growth } from "./components/Growth";
import { Setup } from "./components/Setup";
import { DealFinder } from "./components/DealFinder";
import { Landing } from "./components/Landing";
import { Login } from "./components/Login";
import { Team } from "./components/Team";
import { AcceptInvite } from "./components/AcceptInvite";
import { TeammateWorkspace } from "./components/TeammateWorkspace";
import { Crm } from "./components/Crm";
import { Syndicate } from "./components/Syndicate";
import { Community } from "./components/Community";
import { Learn } from "./components/Learn";
import { Admin } from "./components/Admin";
import { Upsell } from "./components/Upsell";
import { hasFeature } from "./lib/plan";
import { getTeammateId } from "./lib/team";
import { GLOSS, GCATS } from "./lib/glossary";
import { Brand } from "./components/Brand";

type View = "dashboard" | "growth" | "portfolio" | "finder" | "deal" | "glossary" | "setup" | "team" | "crm" | "syndicate" | "community" | "learn" | "admin";
type Depth = "simple" | "standard" | "expert";

export function App() {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [view, setView] = useState<View>("dashboard");
  const [dealId, setDealId] = useState<string | null>(null);
  const [gq, setGq] = useState("");
  const [communityTab, setCommunityTab] = useState<"requests" | undefined>(undefined);
  const [planNonce, setPlanNonce] = useState(0); // re-render gates when the plan changes
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [depth, setDepth] = useState<Depth>("standard");

  // Auth (cloud mode only). Local mode is ready immediately with no account.
  const [session, setSession] = useState<Session | null>(null);
  const [authReady, setAuthReady] = useState(!isCloud());
  const [localOverride, setLocalOverride] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  // Team portal: an invite link (?invite=CODE) or an active teammate seat both
  // route away from the owner app into the teammate experience.
  const [inviteCode, setInviteCode] = useState<string | null>(() => {
    try { return new URLSearchParams(window.location.search).get("invite"); } catch { return null; }
  });
  const [teammate, setTeammate] = useState<string | null>(() => getTeammateId());
  const cleanInviteUrl = () => { try { window.history.replaceState({}, "", window.location.pathname); } catch { /* ignore */ } };

  useEffect(() => {
    if (!isCloud()) return;
    let active = true;
    getSession().then((s) => { if (active) { setSession(s); setAuthReady(true); } });
    const unsub = onAuthChange((s) => setSession(s));
    return () => { active = false; unsub(); };
  }, []);

  // Show the login gate in cloud mode until there's a session (or the user skips).
  const gated = isCloud() && !session && !localOverride;

  useEffect(() => {
    if (!authReady || gated) return;
    setLoading(true);
    listDeals().then((d) => {
      setDeals(d);
      const taylorPark = d.find((deal) => deal.id === "west-dixie-15458");
      if (taylorPark) {
        setDealId(taylorPark.id);
        setView("deal");
      }
    }).finally(() => setLoading(false));
  }, [authReady, gated, session]);

  const handleSignOut = async () => {
    await signOut();
    setSession(null);
    setLocalOverride(false);
    setView("portfolio");
  };

  useEffect(() => {
    document.body.className = depth;
  }, [depth]);

  // Upsell "See plans" navigates to Admin; plan changes re-render the gates.
  useEffect(() => {
    const goEv = (e: Event) => { const d = (e as CustomEvent).detail as View; setView(d); window.scrollTo(0, 0); };
    const planEv = () => setPlanNonce((n) => n + 1);
    window.addEventListener("beacon-go", goEv);
    window.addEventListener("beacon-plan", planEv);
    return () => { window.removeEventListener("beacon-go", goEv); window.removeEventListener("beacon-plan", planEv); };
  }, []);

  const current = deals.find((d) => d.id === dealId) ?? null;

  const patchDeal = useCallback(
    (patch: Partial<Deal>) => {
      if (!current) return;
      const updated = { ...current, ...patch };
      setDeals((ds) => ds.map((d) => (d.id === updated.id ? updated : d)));
      void saveDeal(updated);
    },
    [current]
  );

  const addDeal = () => {
    const d = baseDeal({ name: "New Property " + (deals.length + 1) });
    setDeals((ds) => [...ds, d]);
    void saveDeal(d);
    setDealId(d.id);
    setView("deal");
    setMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const importDeals = (newDeals: Deal[]) => {
    setDeals((ds) => [...ds, ...newDeals]);
    newDeals.forEach((d) => void saveDeal(d));
    setView("portfolio");
    window.scrollTo(0, 0);
  };

  const removeDeal = () => {
    if (!current) return;
    if (!confirm("Delete this deal?")) return;
    const id = current.id;
    setDeals((ds) => ds.filter((d) => d.id !== id));
    void repoDelete(id);
    setView("portfolio");
  };

  const open = (id: string) => {
    setDealId(id);
    setView("deal");
    window.scrollTo(0, 0);
  };

  // Move a deal to a pipeline stage (used by the CRM board).
  const setStage = (id: string, stage: Stage) => {
    const d = deals.find((x) => x.id === id);
    if (!d || d.stage === stage) return;
    const updated = { ...d, stage };
    setDeals((ds) => ds.map((x) => (x.id === id ? updated : x)));
    void saveDeal(updated);
  };

  const go = (v: View) => {
    if (v === "community") setCommunityTab(undefined); // direct nav → default tab
    setView(v);
    setMenuOpen(false);
    window.scrollTo(0, 0);
  };

  // Jump straight into Community → Requests (e.g. from a roadmap course in Learn).
  const goCommunityRequests = () => {
    setCommunityTab("requests");
    setView("community");
    setMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const openTaylorSection = (sectionId: string) => {
    const taylorPark = deals.find((deal) => deal.id === "west-dixie-15458");
    if (!taylorPark) return;
    setDealId(taylorPark.id);
    setView("deal");
    setMenuOpen(false);
    window.setTimeout(() => document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  // Teammate routes take precedence over the owner app / auth gate.
  if (inviteCode) {
    return (
      <AcceptInvite
        code={inviteCode}
        onAccepted={() => { setTeammate(getTeammateId()); setInviteCode(null); cleanInviteUrl(); }}
        onCancel={() => { setInviteCode(null); cleanInviteUrl(); }}
      />
    );
  }
  if (teammate) {
    return <TeammateWorkspace teammateId={teammate} onExit={() => setTeammate(null)} />;
  }

  if (isCloud() && !authReady) {
    return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", color: "var(--muted)" }}>Loading…</div>;
  }
  if (gated) {
    return showLogin
      ? <Login onLocal={() => setLocalOverride(true)} />
      : <Landing onStart={() => setShowLogin(true)} onDemo={() => setLocalOverride(true)} />;
  }

  return (
    <div className="app" data-pn={planNonce}>
      <div className={"scrim" + (menuOpen ? " on" : "")} onClick={() => setMenuOpen(false)} />
      <aside className={"side" + (menuOpen ? " open" : "")}>
        <Brand className="brand" tone="light" />
        <div className="navgrp">Taylor Park</div>
        <NavBtn label="Command center" active={view === "deal"} onClick={() => openTaylorSection("sec-dev-command")} />
        <NavBtn label="Aerial & site" active={false} onClick={() => openTaylorSection("sec-dev-aerial")} />
        <NavBtn label="Existing zoning" active={false} onClick={() => openTaylorSection("sec-dev-zoning")} />
        <NavBtn label="Cost & pro forma" active={false} onClick={() => openTaylorSection("sec-dev-budget")} />
        <NavBtn label="Cash flow & analytics" active={false} onClick={() => openTaylorSection("sec-dev-analytics")} />
        <NavBtn label="Risk & readiness" active={false} onClick={() => openTaylorSection("sec-dev-readiness")} />
        <NavBtn label="Investor report" active={false} onClick={() => openTaylorSection("sec-dev-report")} />

        <div className="navgrp">Project operations</div>
        <NavBtn label="Research library" active={false} onClick={() => openTaylorSection("sec-dev-research")} />
        <NavBtn label="CRM" active={view === "crm"} onClick={() => go("crm")} />
        <NavBtn label="Team" active={view === "team"} onClick={() => go("team")} />

        <div className="navgrp">Account</div>
        <NavBtn label="📖  Glossary" active={view === "glossary"} onClick={() => go("glossary")} />
        <NavBtn label="🧭  Setup" active={view === "setup"} onClick={() => go("setup")} />
        <NavBtn label="⚙️  Admin" active={view === "admin"} onClick={() => go("admin")} />
        <div className="sp" />
        <div className="mini">Reading level</div>
        <div className="depthbar">
          {(["simple", "standard", "expert"] as Depth[]).map((d) => (
            <button key={d} className={depth === d ? "on" : ""} onClick={() => setDepth(d)}>
              {d[0].toUpperCase() + d.slice(1)}
            </button>
          ))}
        </div>
        <span className="badge-mode">{isCloud() ? "CLOUD MODE" : "LOCAL MODE"}</span>
        <div className="mini">
          {isCloud()
            ? "Connected to Supabase — your team's shared workspace."
            : "Running locally — deals save in this browser. Add Supabase keys in .env to go multi-user."}
        </div>
        {session && (
          <div className="mini" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{session.user.email}</span>
            <button className="btn btn-g btn-sm" style={{ padding: "3px 10px", fontSize: 11 }} onClick={handleSignOut}>Sign out</button>
          </div>
        )}
        <div className="mini">Decision-support, not investment advice.</div>
      </aside>

      <main>
        <div className="topstrip">
          <button className="menubtn" onClick={() => setMenuOpen((o) => !o)} aria-label="Menu">☰</button>
          <div>
            <h1>{view === "deal" && current ? current.name : view === "glossary" ? "Glossary" : view === "dashboard" ? "Dashboard" : view === "growth" ? "Growth plan" : view === "setup" ? "Setup" : view === "team" ? "Team" : view === "crm" ? "CRM" : view === "syndicate" ? "Syndicate" : view === "community" ? "Community" : view === "learn" ? "Learn" : view === "admin" ? "Admin" : view === "finder" ? "Deal finder" : "Portfolio"}</h1>
            <div className="sub">
              {view === "deal" && current
                ? (current.addr || current.market) + " · " + current.stage
                : view === "glossary"
                ? "Every term, in plain English, with an example"
                : view === "dashboard"
                ? "Portfolio analytics & the path to your next property"
                : view === "growth"
                ? "Simulate compounding one property into many"
                : view === "setup"
                ? "Your integration checklist & deal-sourcing playbook"
                : view === "team"
                ? "Invite your contractors & inspectors to submit in your format"
                : view === "crm"
                ? "Your pipeline & contacts — the source of truth you own"
                : view === "syndicate"
                ? "Pool capital & expertise with your circle — own it together"
                : view === "community"
                ? "Learn together & share leads — knowledge only, 100% compliant"
                : view === "learn"
                ? "Beacon Academy — courses for you, your team & your community"
                : view === "admin"
                ? "Usage, cost & billing — what every user consumes"
                : view === "finder"
                ? "Paste a list — get a ranked shortlist in one click"
                : deals.length + " properties"}
            </div>
          </div>
          <div className="sp" />
          {view !== "deal" && <button className="btn btn-g btn-sm noprint" onClick={() => openTaylorSection("sec-dev-command")}>Open Taylor Park</button>}
        </div>

        {loading ? (
          <div className="wrap"><p style={{ color: "var(--muted)" }}>Loading…</p></div>
        ) : view === "dashboard" ? (
          <Dashboard deals={deals} onOpen={open} />
        ) : view === "growth" ? (
          <Growth deals={deals} />
        ) : view === "setup" ? (
          <Setup deals={deals} />
        ) : view === "admin" ? (
          <Admin />
        ) : view === "team" ? (
          hasFeature("team") ? <Team /> : <Upsell feature="team" full />
        ) : view === "crm" ? (
          hasFeature("crm") ? <Crm deals={deals} onOpen={open} onStage={setStage} /> : <Upsell feature="crm" full />
        ) : view === "syndicate" ? (
          hasFeature("syndicate") ? <Syndicate deals={deals} onOpenDeal={open} /> : <Upsell feature="syndicate" full />
        ) : view === "community" ? (
          <Community initialTab={communityTab} />
        ) : view === "learn" ? (
          hasFeature("academy") ? <Learn onRequestCourse={goCommunityRequests} /> : <Upsell feature="academy" full />
        ) : view === "finder" ? (
          hasFeature("finder") ? <DealFinder onImport={importDeals} /> : <Upsell feature="finder" full />
        ) : view === "portfolio" ? (
          <Portfolio deals={deals} onOpen={open} onNew={addDeal} />
        ) : view === "deal" && current ? (
          <DealWorkspace deal={current} onChange={patchDeal} onDelete={removeDeal} />
        ) : view === "glossary" ? (
          <GlossaryView q={gq} setQ={setGq} />
        ) : (
          <div className="wrap"><p>Deal not found.</p></div>
        )}
      </main>
    </div>
  );
}

function NavBtn({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button className={"navb" + (active ? " on" : "")} onClick={onClick}>
      {label}
    </button>
  );
}

function GlossaryView({ q, setQ }: { q: string; setQ: (s: string) => void }) {
  const query = q.toLowerCase();
  return (
    <div className="wrap">
      <input id="gq" placeholder="Search a term… (DSCR, limited partner, SPE)" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="note" style={{ margin: "8px 0 4px" }}>
        Quick reference. The full ~108-term version is the companion <b>beacon-glossary.html</b>.
      </div>
      {GCATS.map(([cid, cname]) => {
        const items = GLOSS.filter((g) => g.cat === cid).filter(
          (g) => !query || (g.term + g.acr + g.def + g.ex).toLowerCase().includes(query)
        );
        if (!items.length) return null;
        return (
          <div key={cid}>
            <div className="gcat">{cname}</div>
            {items.map((g) => (
              <div className="gentry" key={g.term}>
                <div>
                  <span className="tm">{g.term}</span>
                  {g.acr && <span className="ac">{g.acr}</span>}
                </div>
                <div className="df">{g.def}</div>
                <div className="ex"><b>Example:</b> {g.ex}</div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
